import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Snackbar from "@mui/material/Snackbar";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { fetchIntegrationSettings, updateIntegrationSettings } from "@/api/settings";
import { formatMoney } from "@/utils/format";
import type { IntegrationSettings } from "@/types/settings";

export function IntegrationsTab() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["integration-settings"], queryFn: fetchIntegrationSettings });
  const [form, setForm] = useState<IntegrationSettings | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (payload: Partial<IntegrationSettings>) => updateIntegrationSettings(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["integration-settings"] });
      setSnackbar("Настройки сохранены.");
    },
  });

  function set<K extends keyof IntegrationSettings>(key: K, value: IntegrationSettings[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  return (
    <QueryState isLoading={isLoading} error={error}>
      {form && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                SMS · Eskiz.uz
              </Typography>
              <StatusChip label={form.smsEnabled ? "Подключено" : "Выключено"} />
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select label="Провайдер" fullWidth value={form.smsProvider ?? "mock"} onChange={(e) => set("smsProvider", e.target.value)}>
                  <MenuItem value="mock">Мок (демо-режим)</MenuItem>
                  <MenuItem value="eskiz">Eskiz.uz</MenuItem>
                  <MenuItem value="playmobile">Play Mobile</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="E-mail кабинета" fullWidth value={form.smsCabinetEmail ?? ""} onChange={(e) => set("smsCabinetEmail", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="API-ключ / пароль"
                  type="password"
                  fullWidth
                  placeholder="вставьте ключ из кабинета Eskiz"
                  value={form.smsApiToken ?? ""}
                  onChange={(e) => set("smsApiToken", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Имя отправителя (sender)" fullWidth value={form.smsSender ?? ""} onChange={(e) => set("smsSender", e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Баланс" fullWidth disabled value={formatMoney(form.smsBalance ?? 0)} helperText="Приходит с провайдера, не редактируется" />
              </Grid>
              <Grid item xs={12} sm={6} display="flex" alignItems="center">
                <FormControlLabel
                  control={<Switch checked={form.smsEnabled} onChange={(e) => set("smsEnabled", e.target.checked)} />}
                  label="Отправлять SMS"
                />
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              Шаблоны сообщений нужно заранее согласовать в кабинете Eskiz — иначе оператор не пропустит рассылку.
            </Typography>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" fontWeight={600}>
                Telegram-бот
              </Typography>
              <StatusChip label={form.telegramNotifyEnabled ? "Подключено" : "Выключено"} />
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Имя бота (@username)"
                  fullWidth
                  value={form.telegramBotUsername ?? ""}
                  onChange={(e) => set("telegramBotUsername", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Токен от @BotFather"
                  type="password"
                  fullWidth
                  placeholder="123456:AA..."
                  value={form.telegramBotToken ?? ""}
                  onChange={(e) => set("telegramBotToken", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} display="flex" alignItems="center">
                <FormControlLabel
                  control={<Switch checked={form.telegramNotifyEnabled} onChange={(e) => set("telegramNotifyEnabled", e.target.checked)} />}
                  label="Дублировать уведомления в Telegram"
                />
              </Grid>
            </Grid>
            <Typography variant="body2" fontWeight={600} mt={2}>
              Что умеет бот
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: "6px 0 0 18px", p: 0, color: "text.secondary" }}>
              <Typography component="li" variant="caption">
                привязка ученика по номеру телефона и коду из SMS
              </Typography>
              <Typography component="li" variant="caption">
                оценки и комментарии преподавателя после каждого урока
              </Typography>
              <Typography component="li" variant="caption">
                посещаемость и пропуски — родителю в тот же день
              </Typography>
              <Typography component="li" variant="caption">
                напоминание об оплате и сумма задолженности
              </Typography>
              <Typography component="li" variant="caption">
                расписание своей группы и отмена урока
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              Подключено учеников: <b>{form.telegramLinkedStudents ?? 0}</b> из {form.totalStudents ?? 0}
            </Typography>
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate(form)}>
              Сохранить настройки
            </Button>
          </Stack>
        </Stack>
      )}
      <Snackbar open={Boolean(snackbar)} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar} />
    </QueryState>
  );
}
